import React from "react";
import { Box, Container, Heading, Text, SimpleGrid, VStack, Icon, Badge, Button, HStack } from "@chakra-ui/react";
import { FaFilePdf, FaDownload, FaBook, FaChartLine, FaHome, FaCalculator, FaNewspaper, FaLightbulb, FaEdit, FaPlayCircle } from "react-icons/fa";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const PropertyResources: React.FC = () => {
  const resources = [
    { title: "Property Investment Guide 2025", desc: "Complete beginner's guide to property investing", icon: FaBook, badge: "NEW", color: "blue" },
    { title: "Market Analysis Report", desc: "Q4 2025 market trends and forecasts", icon: FaChartLine, badge: "POPULAR", color: "green" },
    { title: "Tax Deduction Checklist", desc: "Maximise your investment property deductions", icon: FaFilePdf, badge: null, color: "purple" },
    { title: "First Investment Property", desc: "Step-by-step acquisition roadmap", icon: FaHome, badge: null, color: "orange" },
    { title: "ROI Calculator Template", desc: "Excel spreadsheet for investment analysis", icon: FaCalculator, badge: null, color: "teal" },
    { title: "Suburb Research Toolkit", desc: "Data sources and analysis frameworks", icon: FaLightbulb, badge: null, color: "red" },
    { title: "Finance Comparison Guide", desc: "Compare loan products and structures", icon: FaNewspaper, badge: null, color: "blue" },
    { title: "Solar Value-Add Whitepaper", desc: "Research on solar ROI for investors", icon: FaFilePdf, badge: "NEW", color: "yellow" }
  ];

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/property/resources"
        fallbackTitle="Property Resources"
        fallbackDescription="Educational resources for property investors"
      />
      <NavbarWithCallToAction />

      <Box bgGradient="linear(to-r, indigo.600, purple.600)" color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Icon as={FaBook} boxSize={16} />
            <Heading size="3xl" fontWeight="black">Property Investment Resources</Heading>
            <Text fontSize="xl" maxW="3xl">Free guides, tools, and market insights to help you succeed as a property investor</Text>
          </VStack>
        </Container>
      </Box>

      <Container maxW="container.xl" py={16}>
        <VStack spacing={4} textAlign="center" mb={12}>
          <Heading size="xl">Downloadable Resources</Heading>
          <Text fontSize="lg" color="gray.600">Expert knowledge at your fingertips</Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
          {resources.map((resource) => (
            <Box
              key={resource.title}
              bg="white"
              border="1px"
              borderColor="gray.200"
              borderRadius="xl"
              p={6}
              _hover={{ boxShadow: "lg", transform: "translateY(-4px)" }}
              transition="all 0.3s"
              position="relative"
            >
              {resource.badge && (
                <Badge position="absolute" top={4} right={4} colorScheme={resource.color}>
                  {resource.badge}
                </Badge>
              )}
              <VStack align="stretch" spacing={4}>
                <Icon as={resource.icon} boxSize={12} color={`${resource.color}.500`} />
                <VStack align="stretch" spacing={2}>
                  <Heading size="md" color="gray.800">{resource.title}</Heading>
                  <Text fontSize="sm" color="gray.600">{resource.desc}</Text>
                </VStack>
                <Button
                  leftIcon={<FaDownload />}
                  colorScheme={resource.color}
                  variant="outline"
                  size="sm"
                  mt={2}
                >
                  Download
                </Button>
              </VStack>
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      {/* Featured Webinar */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={8}>
            <VStack spacing={4} textAlign="center">
              <Badge colorScheme="red" fontSize="md" px={4} py={2} borderRadius="full">
                FREE WEBINAR
              </Badge>
              <Heading size="xl">Paying Off Your Mortgage Faster</Heading>
              <Text fontSize="lg" color="gray.600" maxW="2xl">
                Minimise your tax and plan your retirement using investment property equity
              </Text>
            </VStack>

            <Box
              bg="white"
              borderRadius="xl"
              border="2px solid"
              borderColor="red.200"
              p={8}
              shadow="lg"
              maxW="3xl"
              w="full"
              mx="auto"
            >
              <HStack spacing={6} align="flex-start">
                <Icon as={FaPlayCircle} boxSize={12} color="red.500" flexShrink={0} />
                <VStack align="stretch" spacing={4} flex={1}>
                  <VStack align="stretch" spacing={2}>
                    <Heading size="md" color="gray.800">
                      Paying Off Your Mortgage Faster, Minimise Your Tax and Retirement Planning
                    </Heading>
                    <Text fontSize="sm" color="gray.600">On-demand webinar</Text>
                  </VStack>
                  <Text color="gray.700">
                    See how directing $10,000-$20,000 a year into the right investment
                    property has historically built hundreds of thousands of dollars in
                    equity - and how that equity can pay off your home sooner, cut your tax
                    bill, and fund your retirement, benchmarked against decades of Brisbane
                    and regional QLD property growth.
                  </Text>
                  <HStack spacing={3}>
                    <Button
                      as="a"
                      href="/property/resources/webinar-mortgage-tax-retirement"
                      leftIcon={<FaPlayCircle />}
                      colorScheme="red"
                      size="md"
                    >
                      Watch the Webinar
                    </Button>
                  </HStack>
                </VStack>
              </HStack>
            </Box>
          </VStack>
        </Container>
      </Box>

      {/* Forms Section */}
      <Box bg="gradient-to-r" bgGradient="linear(to-r, blue.50, indigo.50)" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={8}>
            <VStack spacing={4} textAlign="center">
              <Badge colorScheme="blue" fontSize="md" px={4} py={2} borderRadius="full">
                FILLABLE FORMS
              </Badge>
              <Heading size="xl">Expression of Interest Form</Heading>
              <Text fontSize="lg" color="gray.600" maxW="2xl">
                Download and complete our fillable PDF forms to get started
              </Text>
            </VStack>

            <Box
              bg="white"
              borderRadius="xl"
              border="2px solid"
              borderColor="blue.200"
              p={8}
              shadow="lg"
              maxW="3xl"
              w="full"
              mx="auto"
            >
              <HStack spacing={6} align="flex-start">
                <Icon as={FaEdit} boxSize={12} color="blue.500" flexShrink={0} />
                <VStack align="stretch" spacing={4} flex={1}>
                  <VStack align="stretch" spacing={2}>
                    <HStack spacing={2}>
                      <Heading size="md" color="gray.800">
                        EOI Sales Information Sheet
                      </Heading>
                      <Badge colorScheme="green">FILLABLE</Badge>
                    </HStack>
                    <Text fontSize="sm" color="gray.600">PDF Form - 1 page</Text>
                  </VStack>
                  <Text color="gray.700">
                    Complete this interactive online form to express your interest in property investment
                    opportunities. Fill it out directly in your browser and download as PDF when complete.
                    The form includes fields for personal information, investment
                    preferences, and financial details.
                  </Text>
                  <HStack spacing={3}>
                    <Button
                      as="a"
                      href="/property/resources/eoi-form"
                      leftIcon={<FaEdit />}
                      colorScheme="blue"
                      size="md"
                    >
                      Fill Out Form
                    </Button>
                    <Button
                      as="a"
                      href="/resources/forms/EOI_Sales_Information_Sheet_v4.pdf"
                      download
                      leftIcon={<FaDownload />}
                      variant="outline"
                      colorScheme="blue"
                      size="md"
                    >
                      Download Blank PDF
                    </Button>
                  </HStack>
                </VStack>
              </HStack>
            </Box>
          </VStack>
        </Container>
      </Box>

      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Heading size="xl">Want More?</Heading>
            <Text fontSize="lg" color="gray.600" maxW="2xl">
              Subscribe to our newsletter for weekly market updates, investment tips, and exclusive resources
            </Text>
            <Button colorScheme="purple" size="lg">Subscribe Now</Button>
          </VStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default PropertyResources;
